import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { StarIcon, MapPinIcon, ClockIcon, CheckCircleIcon, SparklesIcon, EyeIcon, ChatBubbleLeftRightIcon, CurrencyDollarIcon, ScissorsIcon } from '@heroicons/react/24/solid';
import InstantQuoteWizard from '../components/InstantQuoteWizard';
import OffersTray from '../components/OffersTray';
import Logo from '../components/Logo';
import { apiUrl } from '@/config/api';

// Review Form Component
const ReviewForm: React.FC<{ tailorSlug: string; onReviewSubmitted: () => void }> = ({ tailorSlug, onReviewSubmitted }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showAccountConnect, setShowAccountConnect] = useState(false);
  const [isVerifiedUser, setIsVerifiedUser] = useState(false);
  const [masterjiEmail, setMasterjiEmail] = useState('');
  const [masterjiPassword, setMasterjiPassword] = useState('');

  // Use centralized apiUrl helper for calls

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim() || !name.trim() || !email.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl(`/api/tailors/${tailorSlug}/reviews`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating,
          text: comment,
          reviewer_name: name,
          reviewer_email: email,
          is_verified_user: isVerifiedUser,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit review');
      }

      setSubmitSuccess(true);
      setRating(5);
      setComment('');
      setName('');
      setEmail('');
      setIsVerifiedUser(false);
      setShowAccountConnect(false);
      
      // Call the callback to refresh reviews
      setTimeout(() => {
        onReviewSubmitted();
        setSubmitSuccess(false);
      }, 2000);

    } catch (_error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccountConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!masterjiEmail.trim() || !masterjiPassword.trim()) {
      alert('Please enter your MasterJi email and password');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: masterjiEmail,
          password: masterjiPassword,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsVerifiedUser(true);
        setShowAccountConnect(false);
        setMasterjiEmail('');
        setMasterjiPassword('');
        alert('Account connected successfully! Your review will be marked as verified.');
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (_error) {
      console.error('Error connecting account:', error);
      alert('Failed to connect account. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6'
    };
    
    return Array.from({ length: 5 }, (_, i) => (
      <button
        key={i}
        type="button"
        onClick={() => setRating(i + 1)}
        className={`${sizeClasses[size]} ${
          i < rating ? 'text-brand-yellow' : 'text-gray-300'
        } hover:text-brand-yellow transition-colors duration-200`}
      >
        <StarIcon />
      </button>
    ));
  };

  if (submitSuccess) {
    return (
      <div className="text-center py-8">
        <div className="text-green-600 text-6xl mb-4">✓</div>
        <h4 className="text-xl font-semibold text-gray-900 mb-2">Review Submitted!</h4>
        <p className="text-gray-600">Thank you for your feedback. Your review will appear shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Rating */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Your Rating *
        </label>
        <div className="flex items-center space-x-1">
          {renderStars(rating, 'lg')}
          <span className="ml-3 text-sm text-gray-600">{rating} out of 5</span>
        </div>
      </div>

      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Your Name *
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-blue focus:border-transparent"
          placeholder="Enter your name"
          required
        />
      </div>

             {/* Email */}
       <div>
         <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
           Email Address *
         </label>
         <input
           type="email"
           id="email"
           value={email}
           onChange={(e) => setEmail(e.target.value)}
           className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-blue focus:border-transparent"
           placeholder="Enter your email"
           required
         />
       </div>

       {/* Account Connection */}
       <div className="border-t border-gray-200 pt-6">
         <div className="flex items-center justify-between mb-4">
           <div>
             <h4 className="text-sm font-medium text-gray-900">Connect MasterJi Account</h4>
             <p className="text-xs text-gray-500">Get a verified badge on your review</p>
           </div>
           {isVerifiedUser && (
             <div className="flex items-center space-x-1 text-green-600">
               <CheckCircleIcon className="h-4 w-4" />
               <span className="text-xs font-medium">Verified</span>
             </div>
           )}
         </div>
         
         {!isVerifiedUser && (
           <div className="space-y-3">
             <button
               type="button"
               onClick={() => setShowAccountConnect(!showAccountConnect)}
               className="w-full flex items-center justify-center space-x-2 bg-brand-blue/10 text-brand-blue px-4 py-2 rounded-lg hover:bg-brand-blue/20 transition-colors duration-200"
             >
               <span className="text-sm font-medium">
                 {showAccountConnect ? 'Cancel' : 'Connect Account'}
               </span>
             </button>
             
             {showAccountConnect && (
               <form onSubmit={handleAccountConnect} className="space-y-3 p-4 bg-gray-50 rounded-lg">
                 <div>
                   <label className="block text-xs font-medium text-gray-700 mb-1">
                     MasterJi Email
                   </label>
                   <input
                     type="email"
                     value={masterjiEmail}
                     onChange={(e) => setMasterjiEmail(e.target.value)}
                     className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                     placeholder="Enter your MasterJi email"
                     required
                   />
                 </div>
                 <div>
                   <label className="block text-xs font-medium text-gray-700 mb-1">
                     Password
                   </label>
                   <input
                     type="password"
                     value={masterjiPassword}
                     onChange={(e) => setMasterjiPassword(e.target.value)}
                     className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                     placeholder="Enter your password"
                     required
                   />
                 </div>
                 <button
                   type="submit"
                   disabled={isSubmitting}
                   className="w-full bg-brand-blue text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-brand-blue/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   {isSubmitting ? 'Connecting...' : 'Connect Account'}
                 </button>
               </form>
             )}
           </div>
         )}
       </div>

      {/* Comment */}
      <div>
        <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
          Your Review *
        </label>
        <textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-blue focus:border-transparent resize-none"
          placeholder="Share your experience with this tailor..."
          required
        />
        <p className="text-sm text-gray-500 mt-1">
          Minimum 10 characters
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting || comment.length < 10}
          className="bg-brand-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-brand-blue/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span>Submitting...</span>
            </>
          ) : (
            <span>Submit Review</span>
          )}
        </button>
      </div>
    </form>
  );
};

interface TailorProfile {
  id: number;
  slug: string;
  display_name: string;
  tagline: string;
  avatar_url: string;
  workshop_photos: string[];
  social_links: Record<string, string>;
  verified_status: string;
  pickup_radius_km: number;
  address_geo: string;
  template_style: string;
  custom_colors: Record<string, string>;
  layout_preferences: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface PortfolioItem {
  id: number;
  title: string;
  description: string;
  images: string[];
  garment_type: string;
  created_at: string;
  updated_at: string;
}

interface Review {
  id: number;
  rating: number;
  text: string;
  photos: string[];
  reviewer_name?: string;
  reviewer_email?: string;
  is_verified_user?: boolean;
  created_at: string;
  updated_at: string;
}

interface RatingSummary {
  avg_rating: number;
  total_reviews: number;
  rating_distribution: Record<number, number>;
}

const TailorPublicPage: React.FC = () => {
  const { slug: urlSlug } = useParams<{ slug: string }>();
  const [slug, setSlug] = useState(urlSlug || 'test-tailor-123');
  const [profile, setProfile] = useState<TailorProfile | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [ratingSummary, setRatingSummary] = useState<RatingSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showQuoteWizard, setShowQuoteWizard] = useState(false);
  const [quoteRequestId, setQuoteRequestId] = useState<number | null>(null);
  const [showOffersTray, setShowOffersTray] = useState(false);
  const [activeTab, setActiveTab] = useState<'home' | 'services' | 'work' | 'reviews' | 'about'>('home');

  // Railway backend URL
  // deprecated: Api base constant not needed; using apiUrl()

  // Template customization functions
  const getTemplateStyles = () => {
    if (!profile) return {};
    
    // Check for template override in URL
    const urlParams = new URLSearchParams(window.location.search);
    const templateOverride = urlParams.get('template');
    
    // Use default template if template_style is not available
    const template = templateOverride || profile.template_style || 'classic';
    const customColors = profile.custom_colors || {};
    const layoutPrefs = profile.layout_preferences || {};
    
    // Debug: Log the template being applied
    console.log('🎨 Applying template:', template);
    console.log('🎨 Profile template_style:', profile.template_style);
    console.log('🎨 Custom colors:', customColors);
    console.log('🎨 Layout preferences:', layoutPrefs);
    
    // If template fields are not available, use default classic template
    if (!profile.template_style && !profile.custom_colors && !profile.layout_preferences) {
      console.log('Template fields not available, using default classic template');
    }
    
    // Add a visual indicator to the page
    const templateIndicator = document.createElement('div');
    templateIndicator.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: ${template === 'classic' ? '#8B4513' : template === 'modern' ? '#1E40AF' : '#000000'};
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
      z-index: 9999;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    `;
    templateIndicator.textContent = `Template: ${template.toUpperCase()}`;
    templateIndicator.id = 'template-indicator';
    
    // Remove existing indicator
    const existing = document.getElementById('template-indicator');
    if (existing) {
      existing.remove();
    }
    
    // Add new indicator
    document.body.appendChild(templateIndicator);
    
    // Add dramatic background pattern based on template
    const backgroundPattern = document.createElement('div');
    backgroundPattern.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: -1;
      opacity: 0.1;
      ${template === 'classic' ? `
        background-image: repeating-linear-gradient(
          45deg,
          #8B4513,
          #8B4513 10px,
          transparent 10px,
          transparent 20px
        );
      ` : template === 'modern' ? `
        background-image: radial-gradient(
          circle at 20% 80%,
          #1E40AF 0%,
          transparent 50%
        ),
        radial-gradient(
          circle at 80% 20%,
          #64748B 0%,
          transparent 50%
        );
      ` : `
        background-image: linear-gradient(
          90deg,
          #000000 0%,
          transparent 50%,
          #000000 100%
        );
        background-size: 100px 100%;
      `}
    `;
    backgroundPattern.id = 'template-background-pattern';
    
    // Remove existing pattern
    const existingPattern = document.getElementById('template-background-pattern');
    if (existingPattern) {
      existingPattern.remove();
    }
    
    // Add new pattern
    document.body.appendChild(backgroundPattern);
    
    // Default color schemes for each template
    const defaultColors = {
      classic: {
        primary: '#8B4513', // Warm brown
        secondary: '#D2691E', // Orange-brown
        accent: '#FFD700', // Gold
        background: '#F5F5DC', // Beige
        text: '#2F2F2F', // Dark gray
        cardBg: '#FFFFFF',
        cardBorder: '#E5E5E5'
      },
      modern: {
        primary: '#1E40AF', // Deep blue
        secondary: '#64748B', // Slate gray
        accent: '#F59E0B', // Amber
        background: '#F8FAFC', // Light gray
        text: '#1E293B', // Dark slate
        cardBg: '#FFFFFF',
        cardBorder: '#E2E8F0'
      },
      minimal: {
        primary: '#000000', // Pure black
        secondary: '#333333', // Dark gray
        accent: '#666666', // Medium gray
        background: '#FFFFFF', // Pure white
        text: '#000000', // Pure black
        cardBg: '#FAFAFA',
        cardBorder: '#E5E5E5'
      }
    };
    
    // Merge custom colors with template defaults
    const colors = { ...defaultColors[template as keyof typeof defaultColors], ...customColors };
    
    // Ensure we always have valid colors and layout
    const finalColors = {
      primary: colors.primary || defaultColors.classic.primary,
      secondary: colors.secondary || defaultColors.classic.secondary,
      accent: colors.accent || defaultColors.classic.accent,
      background: colors.background || defaultColors.classic.background,
      text: colors.text || defaultColors.classic.text,
      cardBg: colors.cardBg || defaultColors.classic.cardBg,
      cardBorder: colors.cardBorder || defaultColors.classic.cardBorder
    };
    
    const finalLayout = {
      heroStyle: layoutPrefs.heroStyle || 'large',
      photoLayout: layoutPrefs.photoLayout || 'grid',
      contentOrder: layoutPrefs.contentOrder || 'hero-info-portfolio',
      sidebar: layoutPrefs.sidebar !== undefined ? layoutPrefs.sidebar : true
    };
    
    return {
      colors: finalColors,
      layout: finalLayout,
      template
    };
  };

  const applyTemplateStyles = (element: string) => {
    const styles = getTemplateStyles();
    const { colors, layout } = styles;
    
    // Apply different styles based on template and element
    switch (element) {
      case 'hero':
        return {
          backgroundColor: colors.background,
          color: colors.text,
          ...(styles.template === 'classic' && { 
            backgroundImage: 'linear-gradient(135deg, #F5F5DC 0%, #E8E8D0 100%)',
            padding: '4rem 0',
            borderBottom: '3px solid #8B4513',
            position: 'relative',
            overflow: 'hidden',
            fontFamily: 'Georgia, serif',
            textAlign: 'center'
          }),
          ...(styles.template === 'modern' && { 
            backgroundImage: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
            minHeight: '100vh',
            padding: '6rem 0',
            position: 'relative',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            textAlign: 'left'
          }),
          ...(styles.template === 'minimal' && { 
            backgroundColor: '#FFFFFF',
            padding: '2rem 0',
            borderBottom: '1px solid #E5E5E5',
            textAlign: 'center',
            fontFamily: 'Helvetica, Arial, sans-serif',
            minHeight: '50vh'
          })
        };
      case 'primary-button':
        return {
          backgroundColor: colors.primary,
          color: '#FFFFFF',
          fontWeight: '600',
          ...(styles.template === 'classic' && { 
            borderRadius: '0.75rem',
            boxShadow: '0 4px 6px rgba(139, 69, 19, 0.2)',
            border: '2px solid #8B4513',
            padding: '12px 24px',
            fontSize: '16px',
            textTransform: 'none',
            fontFamily: 'Georgia, serif'
          }),
          ...(styles.template === 'modern' && { 
            borderRadius: '0.375rem',
            boxShadow: '0 4px 6px rgba(30, 64, 175, 0.2)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '14px 28px',
            fontSize: '14px',
            fontWeight: '700',
            fontFamily: 'Inter, sans-serif'
          }),
          ...(styles.template === 'minimal' && { 
            borderRadius: '0',
            border: '2px solid #000000',
            boxShadow: 'none',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '400',
            textTransform: 'none',
            fontFamily: 'Helvetica, Arial, sans-serif'
          })
        };
      case 'secondary-button':
        return {
          backgroundColor: 'transparent',
          color: colors.primary,
          border: `2px solid ${colors.primary}`,
          fontWeight: '600',
          ...(styles.template === 'classic' && { 
            borderRadius: '0.75rem',
            backgroundColor: colors.secondary,
            color: '#FFFFFF'
          }),
          ...(styles.template === 'modern' && { 
            borderRadius: '0.375rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }),
          ...(styles.template === 'minimal' && { 
            borderRadius: '0',
            border: '2px solid #000000',
            color: '#000000'
          })
        };
      case 'accent':
        return {
          color: colors.accent,
          ...(styles.template === 'classic' && { 
            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }),
          ...(styles.template === 'modern' && { 
            fontWeight: '700'
          }),
          ...(styles.template === 'minimal' && { 
            fontWeight: '400'
          })
        };
      case 'card':
        return {
          backgroundColor: colors.cardBg,
          borderColor: colors.cardBorder,
          borderWidth: '1px',
          borderStyle: 'solid',
          ...(styles.template === 'classic' && { 
            borderRadius: '1rem',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            borderColor: '#E5E5E5'
          }),
          ...(styles.template === 'modern' && { 
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
            borderColor: '#E2E8F0'
          }),
          ...(styles.template === 'minimal' && { 
            borderRadius: '0',
            boxShadow: 'none',
            borderColor: '#E5E5E5'
          })
        };
      case 'header':
        return {
          backgroundColor: colors.cardBg,
          borderBottom: `1px solid ${colors.cardBorder}`,
          ...(styles.template === 'classic' && { 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }),
          ...(styles.template === 'modern' && { 
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }),
          ...(styles.template === 'minimal' && { 
            boxShadow: 'none'
          })
        };
      case 'navigation':
        return {
          backgroundColor: colors.cardBg,
          borderBottom: `1px solid ${colors.cardBorder}`,
          ...(styles.template === 'classic' && { 
            borderBottom: '2px solid #8B4513'
          }),
          ...(styles.template === 'modern' && { 
            borderBottom: '2px solid #1E40AF'
          }),
          ...(styles.template === 'minimal' && { 
            borderBottom: '1px solid #E5E5E5'
          })
        };
      case 'main-container':
        return {
          ...(styles.template === 'classic' && { 
            fontFamily: 'Georgia, serif',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px'
          }),
          ...(styles.template === 'modern' && { 
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 40px'
          }),
          ...(styles.template === 'minimal' && { 
            fontFamily: 'Helvetica, Arial, sans-serif',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 20px'
          })
        };
      case 'section-spacing':
        return {
          ...(styles.template === 'classic' && { 
            marginBottom: '3rem',
            padding: '2rem 0'
          }),
          ...(styles.template === 'modern' && { 
            marginBottom: '4rem',
            padding: '3rem 0'
          }),
          ...(styles.template === 'minimal' && { 
            marginBottom: '2rem',
            padding: '1rem 0'
          })
        };
      default:
        return {};
    }
  };

  const fetchTailorProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      // Add cache-busting parameter to ensure fresh data
      const timestamp = Date.now();
      const cacheBustedUrl = apiUrl(`/api/business/${slug}?refresh=${timestamp}`);
      
      const response = await fetch(cacheBustedUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setProfile(data.profile);
      setRatingSummary(data.rating_summary);
    } catch (_err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolio = async () => {
    try {
      const response = await fetch(apiUrl(`/api/tailors/${slug}/portfolio`));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPortfolio(data.items);
    } catch (_err) {
      console.error('Failed to fetch portfolio:', err);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(apiUrl(`/api/tailors/${slug}/reviews`));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setReviews(data.reviews);
    } catch (_err) {
      console.error('Failed to fetch reviews:', err);
    }
  };

  useEffect(() => {
    if (urlSlug) {
      setSlug(urlSlug);
    }
  }, [urlSlug]);

  useEffect(() => {
    if (slug) {
      fetchTailorProfile();
      fetchPortfolio();
      fetchReviews();
    }
  }, [slug]);

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6'
    };
    
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`${sizeClasses[size]} ${
          i < rating ? 'text-brand-yellow' : 'text-gray-300'
        }`}
      />
    ));
  };

  const handleQuoteWizardComplete = (result: any) => {
    setQuoteRequestId(result.id);
    setShowQuoteWizard(false);
    setShowOffersTray(true);
  };

  const handleAcceptOffer = (offerId: number) => {
    setShowOffersTray(false);
    // Redirect to order confirmation or dashboard
    alert('Order created successfully! You will receive confirmation details.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-white flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-32 w-32 border-4 border-brand-blue/20 border-t-brand-blue mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <ScissorsIcon className="h-8 w-8 text-brand-blue animate-pulse" />
            </div>
          </div>
          <p className="mt-6 text-gray-600 text-lg font-medium">Loading tailor profile...</p>
          <p className="mt-2 text-gray-500">Preparing your perfect fit experience</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Test with different tailor:
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                placeholder="Enter tailor slug"
              />
              <button
                onClick={fetchTailorProfile}
                className="bg-brand-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-blue/90 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Load Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-brand-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Find Your Perfect Tailor</h2>
            <p className="text-gray-600 mb-6">Enter a tailor's unique identifier to view their profile</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                placeholder="Enter tailor slug"
              />
              <button
                onClick={fetchTailorProfile}
                className="bg-brand-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-blue/90 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Load Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const services = [
    {
      icon: <ScissorsIcon className="w-8 h-8 text-brand-blue" />,
      title: "Bespoke Suits",
      description: "Handcrafted suits tailored to perfection",
      timeline: "7-10 days",
      price: "₹3,000 - ₹8,000"
    },
    {
      icon: <ScissorsIcon className="w-8 h-8 text-brand-blue" />,
      title: "Designer Dresses",
      description: "Elegant dresses for special occasions",
      timeline: "5-7 days",
      price: "₹2,000 - ₹6,000"
    },
    {
      icon: <ScissorsIcon className="w-8 h-8 text-brand-blue" />,
      title: "Formal Shirts",
      description: "Premium cotton shirts with perfect fit",
      timeline: "3-5 days",
      price: "₹800 - ₹2,000"
    },
    {
      icon: <ScissorsIcon className="w-8 h-8 text-brand-blue" />,
      title: "Alterations",
      description: "Professional alterations and adjustments",
      timeline: "1-2 days",
      price: "₹300 - ₹1,500"
    }
  ];

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: getTemplateStyles().colors.background,
        ...applyTemplateStyles('main-container')
      }}
    >
      {/* Template Banner */}
      <div 
        className="w-full py-4 text-center text-white font-bold text-lg"
        style={{
          backgroundColor: getTemplateStyles().template === 'classic' ? '#8B4513' : 
                          getTemplateStyles().template === 'modern' ? '#1E40AF' : '#000000',
          fontFamily: getTemplateStyles().template === 'classic' ? 'Georgia, serif' : 
                     getTemplateStyles().template === 'modern' ? 'Inter, sans-serif' : 'Helvetica, Arial, sans-serif'
        }}
      >
        {getTemplateStyles().template === 'classic' ? '🎨 CLASSIC ELEGANCE' : 
         getTemplateStyles().template === 'modern' ? '🚀 MODERN DESIGN' : '⚡ MINIMAL STYLE'}
      </div>

      {/* Custom Header */}
      <header 
        className="sticky top-0 z-50"
        style={applyTemplateStyles('header')}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <Logo size="md" />
              <div className="flex items-center space-x-1">
                <span className="text-lg font-semibold text-gray-900">{profile.display_name}</span>
                <span className="text-lg text-gray-600">'s</span>
                <span className="text-lg font-bold text-brand-blue">MasterJi</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative py-20"
        style={{
          ...applyTemplateStyles('hero'),
          ...applyTemplateStyles('section-spacing')
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Profile Header */}
              <div className="flex flex-col items-center mb-8">
                <div className="relative mb-6">
                  <img
                    src={profile.avatar_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"}
                    alt={profile.display_name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-xl"
                  />
                  {profile.verified_status === 'verified' && (
                    <div className="absolute -bottom-2 -right-2 bg-success-green rounded-full p-2">
                      <CheckCircleIcon className="h-6 w-6 text-white" />
                    </div>
                  )}
                </div>
                <h1 
                  className="text-4xl md:text-6xl font-bold mb-4"
                  style={{ color: getTemplateStyles().colors?.primary || '#2563EB' }}
                >
                  {profile.display_name}
                </h1>
                <p className="text-xl text-gray-600 mb-6 max-w-3xl">
                  {profile.tagline}
                </p>
                {ratingSummary && (
                  <div className="flex items-center justify-center space-x-4 mb-8">
                    <div className="flex items-center space-x-1">
                      {renderStars(Math.round(ratingSummary.avg_rating), 'lg')}
                      <span className="text-brand-blue font-semibold ml-2 text-lg">
                        {ratingSummary.avg_rating.toFixed(1)}
                      </span>
                    </div>
                    <span className="text-gray-600 text-lg">
                      {ratingSummary.total_reviews} reviews
                    </span>
                  </div>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => setShowQuoteWizard(true)}
                  className="px-8 py-4 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2"
                  style={applyTemplateStyles('primary-button')}
                >
                  <SparklesIcon className="h-5 w-5" />
                  <span>Request a Quote</span>
                </button>
                <button
                  onClick={() => setActiveTab('work')}
                  className="px-8 py-4 font-semibold transition-all duration-200 flex items-center space-x-2 border-2"
                  style={applyTemplateStyles('secondary-button')}
                >
                  <EyeIcon className="h-5 w-5" />
                  <span>View Work</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section 
        className="sticky top-16 z-10"
        style={applyTemplateStyles('navigation')}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'home', label: 'Home' },
              { id: 'services', label: 'Services' },
              { id: 'work', label: 'Work' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'about', label: 'About' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-transparent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={{
                  ...(activeTab === tab.id && {
                    borderBottomColor: getTemplateStyles().colors?.primary || '#2563EB',
                    color: getTemplateStyles().colors?.primary || '#2563EB'
                  })
                }}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'home' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Services Overview */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                    style={applyTemplateStyles('card')}
                  >
                    <div 
                      className="flex items-center justify-center w-12 h-12 rounded-lg mb-4"
                      style={{ backgroundColor: `${getTemplateStyles().colors?.primary || '#2563EB'}20` }}
                    >
                      <div style={{ color: getTemplateStyles().colors?.primary || '#2563EB' }}>
                        {service.icon}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {service.timeline}
                      </span>
                      <span className="flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                        {service.price}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Featured Work */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Featured Work</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {portfolio.slice(0, 3).map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
                  >
                    {item.images && item.images.length > 0 && (
                      <div className="relative overflow-hidden">
                        <img
                          src={item.images[0]}
                          alt={item.title}
                          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="font-semibold text-gray-900 mb-2 text-lg">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.description}</p>
                      <span className="inline-block bg-brand-blue/10 text-brand-blue text-xs px-3 py-1 rounded-full font-medium">
                        {item.garment_type}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Social Proof */}
            {reviews.length > 0 && (
              <section className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Clients Say</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reviews.slice(0, 3).map((review, index) => (
                    <motion.div
                      key={review.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="bg-white rounded-xl p-6 shadow-lg border border-gray-200"
                    >
                      <div className="flex items-center space-x-1 mb-3">
                        {renderStars(review.rating, 'sm')}
                      </div>
                      <p className="text-gray-700 mb-3 text-sm line-clamp-3">{review.text}</p>
                      <div className="text-xs text-gray-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Trust Indicators */}
            <section className="text-center">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-brand-blue mb-2">15+</div>
                  <div className="text-gray-600">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-brand-blue mb-2">500+</div>
                  <div className="text-gray-600">Happy Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-brand-blue mb-2">{profile.pickup_radius_km}km</div>
                  <div className="text-gray-600">Service Radius</div>
                </div>
              </div>
            </section>
          </motion.div>
        )}

        {activeTab === 'services' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-lg border border-gray-200"
                >
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-16 h-16 bg-brand-blue/10 rounded-xl mr-4">
                      {service.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-gray-900">{service.title}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Timeline:</span>
                      <span className="font-semibold">{service.timeline}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Starting Price:</span>
                      <span className="font-semibold text-brand-blue">{service.price}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowQuoteWizard(true)}
                    className="w-full mt-6 bg-brand-blue text-white py-3 px-6 rounded-lg font-semibold hover:bg-brand-blue/90 transition-all duration-200"
                  >
                    Get a Quote
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'work' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Portfolio</h2>
            {portfolio.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {portfolio.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
                  >
                    {item.images && item.images.length > 0 && (
                      <div className="relative overflow-hidden">
                        <img
                          src={item.images[0]}
                          alt={item.title}
                          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="font-semibold text-gray-900 mb-2 text-lg">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.description}</p>
                      <span className="inline-block bg-brand-blue/10 text-brand-blue text-xs px-3 py-1 rounded-full font-medium">
                        {item.garment_type}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <EyeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No portfolio items yet</h3>
                <p className="text-gray-500">This tailor hasn't uploaded their work yet.</p>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'reviews' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Client Reviews</h2>
            
            {/* Add Review Section */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <StarIcon className="h-6 w-6 text-brand-blue mr-2" />
                Write a Review
              </h3>
              <ReviewForm tailorSlug={slug} onReviewSubmitted={() => {
                // Refresh reviews after submission
                fetchReviews();
              }} />
            </div>

            {/* Existing Reviews */}
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What Others Say</h3>
            {reviews.length > 0 ? (
              <div className="space-y-6">
                {reviews.map((review, index) => (
                  <motion.div
                    key={review.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 shadow-lg border border-gray-200"
                  >
                                         <div className="flex items-start space-x-4">
                       <div className="flex-shrink-0">
                         <div className="w-12 h-12 bg-brand-blue rounded-full flex items-center justify-center">
                           <span className="text-white font-semibold text-sm">
                             {review.reviewer_name ? review.reviewer_name.charAt(0).toUpperCase() : review.text.charAt(0).toUpperCase()}
                           </span>
                         </div>
                       </div>
                       <div className="flex-1">
                                                   <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              {renderStars(review.rating, 'sm')}
                              <span className="text-sm text-gray-500">
                                {new Date(review.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {review.reviewer_name && (
                                <span className="text-sm font-medium text-gray-900">
                                  {review.reviewer_name}
                                </span>
                              )}
                              {review.is_verified_user && (
                                <div className="flex items-center space-x-1 text-green-600">
                                  <CheckCircleIcon className="h-3 w-3" />
                                  <span className="text-xs font-medium">Verified</span>
                                </div>
                              )}
                            </div>
                          </div>
                         <p className="text-gray-700 mb-3 leading-relaxed">{review.text}</p>
                        {review.photos && review.photos.length > 0 && (
                          <div className="flex space-x-2">
                            {review.photos.map((photo, photoIndex) => (
                              <img
                                key={photoIndex}
                                src={photo}
                                alt={`Review photo ${photoIndex + 1}`}
                                className="w-16 h-16 object-cover rounded-lg"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <StarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                <p className="text-gray-500">Be the first to review this tailor!</p>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'about' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">About {profile.display_name}</h2>
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Our Story</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With over 15 years of experience in the art of tailoring, we specialize in creating 
                  bespoke garments that perfectly fit your style and personality. Our commitment to 
                  quality craftsmanship and attention to detail has earned us the trust of hundreds 
                  of satisfied customers.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We believe that every garment should be a reflection of the wearer's personality and 
                  lifestyle. From the initial consultation to the final fitting, we ensure that every 
                  detail is perfect.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <MapPinIcon className="h-5 w-5 mr-2 text-brand-blue" />
                    Location & Service Area
                  </h4>
                  <p className="text-gray-600">Delhi, India</p>
                  <p className="text-sm text-gray-500 mt-1">Service radius: {profile.pickup_radius_km}km</p>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2 text-brand-blue" />
                    Typical Turnaround Time
                  </h4>
                  <p className="text-gray-600">5-7 days for most garments</p>
                  <p className="text-sm text-gray-500 mt-1">Express service available</p>
                </div>
              </div>

              {/* Workshop Photos */}
              {profile.workshop_photos && profile.workshop_photos.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <EyeIcon className="h-5 w-5 mr-2 text-brand-blue" />
                    Our Workshop
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {profile.workshop_photos.map((photo, index) => (
                      <div key={index} className="group relative overflow-hidden rounded-lg">
                        <img
                          src={photo}
                          alt={`Workshop photo ${index + 1}`}
                          className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Social Links */}
              {profile.social_links && Object.keys(profile.social_links).length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-brand-blue" />
                    Connect With Us
                  </h4>
                  <div className="flex space-x-4">
                    {Object.entries(profile.social_links).map(([platform, link]) => (
                      <a
                        key={platform}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 bg-gray-100 hover:bg-brand-blue/10 text-gray-700 hover:text-brand-blue px-4 py-2 rounded-lg transition-all duration-200"
                      >
                        <span className="capitalize font-medium">{platform}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Sticky CTA for Mobile */}
      <div className="fixed bottom-4 left-4 right-4 md:hidden z-50">
        <button
          onClick={() => setShowQuoteWizard(true)}
          className="w-full bg-brand-blue text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
        >
          Request a Quote
        </button>
      </div>

      {/* Simple Footer */}
      <footer className="bg-brand-blue text-brand-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-brand-white/80">
              Powered by{' '}
              <a 
                href="https://www.master-ji.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-brand-white font-semibold hover:text-brand-white/80 transition-colors duration-200"
              >
                MasterJi
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Quote Wizard Modal */}
      {showQuoteWizard && (
        <InstantQuoteWizard
          onComplete={handleQuoteWizardComplete}
          onClose={() => setShowQuoteWizard(false)}
        />
      )}

      {/* Offers Tray */}
      {showOffersTray && quoteRequestId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Quote Offers</h2>
                <button
                  onClick={() => setShowOffersTray(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <OffersTray
                quoteRequestId={quoteRequestId}
                onAcceptOffer={handleAcceptOffer}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TailorPublicPage;
